import { useEffect, useState, useMemo } from 'react'
import {
  faHouseChimney,
  faBook,
  faBooks,
  faFileCertificate,
  faFileImport,
  faComments,
  faUserLock,
  faGear,
  faPenToSquare,
  faChartLine,
  faCloudUpload,
  faCodeBranch,
  IconDefinition,
  faGraduationCap,
  faFileChartColumn,
  faVideo,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getIntegration } from 'api/integrations'
import { IntegrationZoom } from 'models/integrations'
import { useReaderMessage } from 'contexts/ReaderMessageContext'
import { getToken } from 'helpers/cookies.helper'
import { IMainState } from '../../../store/states'
import { allRoles, admins, UserRoles } from './constants'

const { ADMINISTRATOR, MODERATOR, COLLABORATOR, SUPERVISOR } = UserRoles

export interface IMenuItem {
  active: boolean
  icon?: IconDefinition
  roles: UserRoles[] | readonly UserRoles[]
  text: string
  url?: string
  badgeValue?: number
  subItems?: IMenuItem[]
}

export const useMenuItems = () => {
  const { t } = useTranslation()
  const [menuItems, setMenuItems] = useState<IMenuItem[]>([])
  const [activeMenuItem, setActiveMenuItem] = useState(0)
  const [activeSubMenuItem, setActiveSubMenuItem] = useState(0)
  const [integrationZoom, setIntegrationZoom] = useState<IntegrationZoom>()
  const { unReadMessages } = useReaderMessage()
  const { permission } = useSelector(({ user }: IMainState) => user)
  const generalPermissions = useSelector(
    ({ general }: IMainState) => general.permissions
  )

  const rol = useMemo(() => {
    if (permission.superadmin) return UserRoles.SUPER_ADMINISTRATOR
    if (permission.admin) return UserRoles.ADMINISTRATOR
    if (permission.moderator) return UserRoles.MODERATOR
    if (permission.supervisor) return UserRoles.SUPERVISOR
    return UserRoles.COLLABORATOR
  }, [permission])

  useEffect(() => {
    const fetchZoom = async () => {
      try {
        const result = await getIntegration('zoom')
        if (result?.active) {
          setIntegrationZoom(result as IntegrationZoom)
        }
      } catch (error) {
        console.error('Error fetching Zoom integration:', error)
      }
    }

    fetchZoom()
  }, [])

  useEffect(() => {
    const items = [
      {
        active: true,
        roles: allRoles,
        text: t('commons.sidebar.menu.sections.learningCenter'),
      },
      {
        active: true,
        icon: faHouseChimney,
        roles: allRoles,
        text: t('commons.sidebar.menu.home'),
        url: '/',
      },
      {
        active: true,
        icon: faBook,
        roles: [ADMINISTRATOR, MODERATOR, COLLABORATOR, SUPERVISOR],
        text: t('commons.sidebar.menu.myCourses'),
        url: '/my-courses',
      },
      {
        active: true,
        icon: faBooks,
        roles: allRoles,
        text: t('commons.sidebar.menu.library'),
        url: '/library',
      },
      {
        active: true,
        icon: faFileCertificate,
        roles: allRoles,
        text: t('commons.sidebar.menu.myCertificates'),
        url: '/certificates',
      },
      {
        active: true,
        icon: faFileImport,
        roles: allRoles,
        text: t('commons.sidebar.menu.myExternalCourses'),
        url: '/external-courses',
      },
      {
        active: true,
        icon: faComments,
        roles: allRoles,
        text: t('commons.sidebar.menu.myMessages'),
        url: '/messages',
        badgeValue: unReadMessages,
      },
      {
        active: integrationZoom?.active || false,
        icon: faVideo,
        roles: [...admins],
        text: t('commons.sidebar.menu.webinars'),
        url: '/webinars',
      },
      {
        active: true,
        roles: allRoles,
        text: t('commons.sidebar.menu.sections.management'),
      },
      {
        active: true,
        icon: faGear,
        roles: [...admins],
        text: t('commons.sidebar.menu.configuration'),
        url: '/configuration',
        subItems: [
          {
            active: true,
            roles: [],
            text: t('commons.sidebar.menu.settings.participants'),
            url: '/configuration/users',
          },
          {
            active: true,
            roles: [],
            text: t('commons.sidebar.menu.settings.areasHierarchy'),
            url: '/configuration/hierarchy',
          },
          {
            active: true,
            roles: [],
            text: t('commons.sidebar.menu.settings.profiles'),
            url: '/configuration/lms-profiles',
          },
          {
            active: true,
            roles: [],
            text: t('commons.sidebar.menu.settings.translations'),
            url: '/configuration/translations',
          },
          {
            active: true,
            roles: [],
            text: t('commons.sidebar.menu.settings.integrations'),
            url: '/configuration/integrations',
          },
        ].filter((item) => item.active),
      },
      {
        active: true,
        icon: faPenToSquare,
        roles: [...admins, MODERATOR],
        text: t('commons.sidebar.menu.courseManagement'),
        url: '/administration/courses',
        subItems: [
          {
            active: true,
            roles: [...admins],
            text: t('commons.sidebar.menu.coursesManagement.courses'),
            url: '/administration/courses',
          },
          {
            active: true,
            roles: [...admins],
            text: t('commons.sidebar.menu.coursesManagement.certificates'),
            url: '/administration/courses/templates',
          },
          {
            active: true,
            roles: [...admins],
            text: t('commons.sidebar.menu.coursesManagement.externalCourses'),
            url: '/administration/courses/external',
          },
        ].filter((item) => item.active),
      },
      {
        active: true,
        icon: faGraduationCap,
        roles: [...admins],
        text: t('commons.sidebar.menu.learningPaths'),
        url: '/administration/learning-paths',
      },
      {
        active: true,
        icon: faUserLock,
        roles: [...admins, SUPERVISOR],
        text: t('commons.sidebar.menu.adminRequests'),
        url: '/admin-applications',
      },
      {
        active: generalPermissions.enableWorkflows,
        icon: faCodeBranch,
        roles: [...admins, MODERATOR, COLLABORATOR, SUPERVISOR],
        text: t('commons.sidebar.menu.internalRequests'),
        url: '/requests',
      },
      {
        active: true,
        icon: faChartLine,
        roles: [...admins, MODERATOR],
        text: t('commons.sidebar.menu.monitoring'),
        url: '/administration/monitoring',
      },
      {
        active: true,
        icon: faFileChartColumn,
        roles: [...admins],
        text: t('commons.sidebar.menu.reports'),
        url: '/reports',
      },
      {
        active: true,
        icon: faCloudUpload,
        roles: [...admins, MODERATOR],
        text: t('commons.sidebar.menu.importCenterV2'),
        url: '/import-center',
      },
    ].filter(
      (item) =>
        item.active &&
        (item.roles.includes(rol) ||
          getToken().is_admin ||
          getToken().is_superadmin)
    )
    setMenuItems(items)
    let activeIndex = 0
    items.forEach((item, index) => {
      if (item.url && window.location.pathname.includes(item.url)) {
        activeIndex = index
        let subActiveIndex = 0
        item.subItems?.forEach((subItem, subIndex) => {
          if (subItem.url && window.location.pathname.includes(subItem.url)) {
            subActiveIndex = subIndex
          }
        })
        setActiveSubMenuItem(subActiveIndex)
      }
    })
    setActiveMenuItem(activeIndex)
  }, [
    unReadMessages,
    generalPermissions.enableWorkflows,
    integrationZoom?.active,
    window.location.pathname,
    rol,
  ])

  return { activeMenuItem, menuItems, activeSubMenuItem, setActiveSubMenuItem }
}
